import PropTypes from "prop-types"
import { useState, useContext, useEffect } from "react"
import { gql, useQuery } from "@apollo/client"
import _ from "lodash"
import { Field, useFormikContext } from "formik"
import { MultiSelect } from "primereact/multiselect"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"
import styles from "../../../styles/Form.module.css"

function ManageProgramIdentityIds({ field, block, index }) {
  const [activePrograms, setActivePrograms] = useState([])
  const [inactivePrograms, setInactivePrograms] = useState([])

  const [visible, setVisible] = useState("block")

  const checkIfRequired = () => {
    try {
      return JSON.parse(field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }

  const isRequired = checkIfRequired()
  const formik = useFormikContext()
  const form = useContext(FormContext)
  const userCtx = useContext(UserContext)

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  const GET_PROGRAMS = gql`
    query getPrograms(
      $staff_id: ID!
      $organization_id: ID!
      $participant_membership_id: ID!
    ) {
      programsByOrganization(
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        key: program_identity_id
        label: program_id_name
        status: program_id_status
      }
      programsByMembershipId(
        staff_id: $staff_id
        organization_id: $organization_id
        participant_membership_id: $participant_membership_id
      ) {
        key: program_identity_id
        label: program_id_name
      }
    }
  `

  const { loading } = useQuery(GET_PROGRAMS, {
    fetchPolicy: "network-only",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      participant_membership_id: userCtx.participant_membership_id,
    },
    skip: !userCtx,
    onCompleted: ({ programsByOrganization, programsByMembershipId }) => {
      if (!_.isEmpty(programsByOrganization)) {
        const activeProgramsList =
          programsByOrganization
            .filter((program) => program.status === "ACTIVE")
            .map((program) => {
              delete program.status
              return program
            }) || []
        const inactiveProgramsList =
          programsByOrganization
            .filter((program) => program.status === "INACTIVE")
            .map((program) => {
              delete program.status
              return program
            }) || []

        setActivePrograms(activeProgramsList)
        setInactivePrograms(inactiveProgramsList)

        formik.setFieldValue(field.field_name, programsByMembershipId)
      } else {
        formik.setFieldValue(field.field_name, null)
      }
    },
  })

  useEffect(() => {
    if (block === "participant__manage_services") {
      const manageServicesType = formik?.getFieldProps("manage_services_type")
      setVisible("none")
      if (
        manageServicesType.value === "Program ID assignment" ||
        manageServicesType.value === "Program ID reassignment"
      ) {
        setVisible("block")
      }
    } else {
      setVisible("block")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik])

  const handleSelectProgramIds = (value) => {
    if (value === null) {
      formik.setFieldValue(field.field_name, "")
    } else {
      const fieldCurrentValue = formik.values[field.field_name] || []
      // inActivePrograms should remain as they are, is handle with another function
      const currentInActivePrograms =
        fieldCurrentValue.filter((item) => {
          return inactivePrograms.find(
            (inactiveItem) => inactiveItem.key === item.key
          )
        }) || []
      const newValue = [...currentInActivePrograms, ...value]
      formik.setFieldValue(field.field_name, newValue)
    }
  }

  const handleDiselectInactiveProgramIds = (value) => {
    // Empty array means all programs are deselected
    if (value.length === 0) {
      const selectedPrograms = formik.values[field.field_name].filter((item) =>
        activePrograms.find((activeItem) => activeItem.key === item.key)
      )
      formik.setFieldValue(field.field_name, selectedPrograms)
    } else {
      const fieldCurrentValue = formik.values[field.field_name]
      // activePrograms should remain as they are
      const currentActivePrograms = fieldCurrentValue.filter((item) => {
        return activePrograms.find((activeItem) => activeItem.key === item.key)
      })
      // value is the deselected inactive programs
      const filteredPrograms = [...currentActivePrograms, ...value]
      setInactivePrograms(value)
      formik.setFieldValue(field.field_name, filteredPrograms)
    }
  }

  const handleMultiSelectRender = (formikField) => {
    const currentFormValues = formikField?.value || []

    const currentInactiveSelectedPrograms = currentFormValues.filter((item) =>
      inactivePrograms.find((inactiveItem) => inactiveItem.key === item.key)
    )

    const renderInactivePrograms = () => {
      const displayInactiveProgramList =
        currentInactiveSelectedPrograms.length > 0

      if (!displayInactiveProgramList) return null

      return (
        <div>
          <label className={styles.label}>Inactive Programs ID(s)</label>
          <div className={styles.description}>
            Inactive programs can only be deselected.
          </div>
          <MultiSelect
            placeholder="Unselect only"
            id={field.field_name}
            name={formikField.name}
            value={currentInactiveSelectedPrograms}
            onChange={(e) => handleDiselectInactiveProgramIds(e.value)}
            onBlur={(e) => formik.handleBlur(e)}
            disabled={isDisabled}
            options={currentInactiveSelectedPrograms}
          />
        </div>
      )
    }

    const currentActiveSelectedPrograms = currentFormValues.filter((item) =>
      activePrograms.find((activeItem) => activeItem.key === item.key)
    )

    return (
      <>
        <MultiSelect
          placeholder="Please select..."
          id={field.field_name}
          name={formikField.name}
          value={currentActiveSelectedPrograms}
          onChange={(e) => handleSelectProgramIds(e.value)}
          onBlur={(e) => formik.handleBlur(e)}
          disabled={isDisabled}
          options={activePrograms}
        />

        {renderInactivePrograms()}
      </>
    )
  }

  if (loading) return null

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: visible,
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <Field name={field.field_name}>
        {({ field: formikField, meta, form }) => (
          <>
            {handleMultiSelectRender(formikField)}
            {meta.touched && meta.error && (
              <div className={styles.errFieldMsg}>{meta.error}</div>
            )}
          </>
        )}
      </Field>
    </div>
  )
}

ManageProgramIdentityIds.propTypes = {
  field: PropTypes.shape({
    field_name: PropTypes.string.isRequired,
    field_label: PropTypes.string.isRequired,
    field_description: PropTypes.string,
    field_validation: PropTypes.shape({
      required: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    read_only: PropTypes.bool,
  }).isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
}

export default ManageProgramIdentityIds
